import React from 'react';

import {SAMPLE_TYPE_DISPLAY} from 'constants/SampleType';
import * as ModalContainer from 'ui-library/ModalContainer';
import {ImportedRow} from 'workflows/samples/makeSampleGroup';

import {configureMapColumnNamesModalPane} from './panes/MapColumnNamesModalPane';
import ShowErrorsModalPane from './panes/ShowErrorsModalPane';
import UploadSpreadsheetModalPane from './panes/UploadSpreadsheetModalPane';
import * as SpreadsheetBulkImportActions from './SpreadsheetBulkImportActions';
import {
  COLUMN_TYPE,
  IMPORT_TYPE,
  Pane,
  PaneKey,
  SPREADSHEET_BULK_IMPORT_KEY,
  SpreadsheetColumn,
} from './SpreadsheetBulkImportConstants';
import SpreadsheetBulkImportModalContainer from './SpreadsheetBulkImportModalContainer';
import {findDuplicateColumn} from './SpreadsheetBulkImportUtil';

function isDuplicate(col1: SpreadsheetColumn, col2: SpreadsheetColumn): boolean {
  if (
    col1.type !== COLUMN_TYPE.SAMPLE_GROUP_SCHEMA_FIELD ||
    col2.type !== COLUMN_TYPE.SAMPLE_GROUP_SCHEMA_FIELD
  ) {
    return false;
  }
  return col1.schemaField?.id === col2.schemaField?.id && col1.sampleType === col2.sampleType;
}

export interface BulkAddSampleGroupsFlowOpenProps {
  onImportRows: (rows: Array<ImportedRow>) => void;
  sampleGroupSchemaId: string;
}

export interface BulkAddSampleGroupsFlowProps extends BulkAddSampleGroupsFlowOpenProps {
  onRequestClose: () => void;
}

export default class BulkAddSampleGroupsFlow extends React.Component<BulkAddSampleGroupsFlowProps> {
  static open(props: BulkAddSampleGroupsFlowOpenProps): void {
    ModalContainer.open(
      SPREADSHEET_BULK_IMPORT_KEY,
      <BulkAddSampleGroupsFlow {...props} onRequestClose={SpreadsheetBulkImportActions.closeModal} />
    );
  }

  private getPanes = (): Array<Pane> => [
    {
      paneKey: PaneKey.UPLOAD_SPREADSHEET,
      configuration: UploadSpreadsheetModalPane.configure({}),
    },
    {
      paneKey: PaneKey.MAP_COLUMN_NAMES,
      configuration: configureMapColumnNamesModalPane({
        getValidColumnTypes() {
          return [COLUMN_TYPE.SAMPLE_GROUP_SCHEMA_FIELD, COLUMN_TYPE.IGNORE];
        },
        getRequiredColumnsError(columns: Array<SpreadsheetColumn>): string | null {
          const duplicateColumn = findDuplicateColumn(columns, isDuplicate);
          if (!duplicateColumn) {
            return null;
          }

          const {schemaField, sampleType} = duplicateColumn;
          if (!sampleType || !schemaField) {
            return null;
          }

          const displaySampleType = sampleType ? SAMPLE_TYPE_DISPLAY[sampleType] : '';
          return `"Set ${schemaField.name} ${displaySampleType}" found more than once`;
        },
        onValidateSpreadsheet: SpreadsheetBulkImportActions.validateNewSampleGroups,
      }),
    },
    {
      paneKey: PaneKey.SHOW_ERRORS,
      configuration: ShowErrorsModalPane.configure({
        enableReplaceLinkWithText: false,
        enableSkipErrors: false,
      }),
    },
  ];

  private handleImportFromSpreadsheet = (): void => {
    SpreadsheetBulkImportActions.getNewSampleGroups(this.props.onImportRows);
  };

  render(): JSX.Element {
    const {sampleGroupSchemaId, onRequestClose} = this.props;
    // TODO: "Import Request Samples" should be generic based on caller, probably same with import type
    return (
      <SpreadsheetBulkImportModalContainer
        importType={IMPORT_TYPE.REQUEST_SAMPLES}
        initialSettings={{
          initialSampleGroupSchemaId: sampleGroupSchemaId,
        }}
        onImportFromSpreadsheet={this.handleImportFromSpreadsheet}
        onRequestClose={onRequestClose}
        panes={this.getPanes()}
        title="Import request samples"
      />
    );
  }
}
